import React, { useState, useEffect, useCallback, useRef } from 'react';
import { useAuth } from '../../contexts/AuthContext';
import TimeSeriesChart from '../TimeSeriesChart';
import GroupTimeSeriesChart from '../GroupTimeSeriesChart';
import ComparisonChart from '../ComparisonChart';
import ReportSummary from '../ReportSummary';
import { Folder } from 'lucide-react';
import genericClusteringService from '../../services/genericClusteringService';

const CreativeAttributes = ({ dashboardData, filters, onFilterChange }) => {
  const { getAccountId } = useAuth();
  const [loading, setLoading] = useState(false);
  const [featureAnalysisData, setFeatureAnalysisData] = useState(null);
  const [topAdsForChart, setTopAdsForChart] = useState([]);
  const [previewModal, setPreviewModal] = useState({ isOpen: false, assetUrl: '', mediaFormat: '', adName: '' });
  const [adsModal, setAdsModal] = useState({ isOpen: false, combination: null, ads: [], loading: false });
  const [analysisProgress, setAnalysisProgress] = useState(0);
  const [selectedAnalysisType, setSelectedAnalysisType] = useState('singleFeatures');
  const [mediaType, setMediaType] = useState('video'); // Default to video
  const [viewMode, setViewMode] = useState('card'); // 'card', 'time-trend', 'comparison'
  
  // Use correlation filter from global Dashboard Configuration
  const correlationMetric = filters?.correlationMetric || 'revenue';
  
  const lastRequestParams = useRef(null);
  const isInitialMount = useRef(true);
  const abortControllerRef = useRef(null);
  const debounceTimeoutRef = useRef(null);

  // Extract date range info from filters
  const getDateRangeInfo = () => {
    console.log('🔍 CreativeAttributes getDateRangeInfo - filters:', {
      dateRangeType: filters?.dateRangeType,
      startDate: filters?.startDate,
      endDate: filters?.endDate,
      dateRange: filters?.dateRange
    });
    
    // Check for custom date range
    if (filters?.dateRangeType === 'custom' && filters?.startDate && filters?.endDate) {
      // Calculate days for custom range
      const start = new Date(filters.startDate + 'T00:00:00');
      const end = new Date(filters.endDate + 'T00:00:00');
      const calculatedDays = Math.ceil((end - start) / (1000 * 60 * 60 * 24)) + 1;
      
      console.log('✅ CreativeAttributes returning CUSTOM date range:', {
        startDate: filters.startDate,
        endDate: filters.endDate,
        calculatedDays
      });
      
      return {
        type: 'custom',
        startDate: filters.startDate,
        endDate: filters.endDate,
        days: calculatedDays // Include calculated days for display purposes
      };
    }
    // Default to preset days
    const days = (filters?.dateRange && typeof filters.dateRange === 'number') ? filters.dateRange : 7;
    
    console.log('📅 CreativeAttributes returning PRESET date range:', days, 'days');
    
    return {
      type: 'preset',
      days: days,
      startDate: null,
      endDate: null
    };
  };

  const getAdNameFilter = () => {
    console.log('🔍 CreativeAttributes getAdNameFilter - filters:', filters);
    console.log('🔍 adNameFilterKeywords:', filters?.adNameFilterKeywords);
    
    if (filters?.adNameFilterKeywords && filters.adNameFilterKeywords.length > 0) {
      const adNameFilter = {
        keywords: filters.adNameFilterKeywords.filter(k => k.trim()),
        logic: filters.adNameFilterLogic || 'AND',
        mode: filters.adNameFilterMode || 'include'
      };
      console.log('🔍 CreativeAttributes returning adNameFilter:', adNameFilter);
      return adNameFilter;
    }
    console.log('🔍 CreativeAttributes: No ad name filter');
    return null;
  };

  const getMediaType = () => {
    return mediaType;
  };

  // Run Feature Analysis
  const runFeatureAnalysis = useCallback(async (force = false) => {
    const accountId = getAccountId();
    if (!accountId) {
      return;
    }

    const dateRangeInfo = getDateRangeInfo();
    const adNameFilter = getAdNameFilter();
    const mediaType = getMediaType();

    // Create request signature
    const requestParams = JSON.stringify({ accountId, dateRangeInfo, adNameFilter, mediaType, correlationMetric });
    
    // Prevent duplicate requests with same parameters (unless forced)
    if (!force && loading) {
      return;
    }
    
    if (!force && lastRequestParams.current === requestParams && featureAnalysisData) {
      return;
    }
    
    // Cancel any existing request
    if (abortControllerRef.current) {
      abortControllerRef.current.abort();
    }
    
    // Create new abort controller
    abortControllerRef.current = new AbortController();
    
    setLoading(true);
    setAnalysisProgress(0);
    
    // Start progress bar
    const progressInterval = setInterval(() => {
      setAnalysisProgress(prev => {
        if (prev >= 100) {
          clearInterval(progressInterval);
          return 100;
        }
        return prev + (100 / 300); // 30 seconds
      });
    }, 100);
    
    try {
      // Call Feature Analysis API - build params based on date range type
      const params = new URLSearchParams({
        minSpend: '50',
        minImpressions: '10',
        correlationMetric: correlationMetric || 'revenue',
        analysisType: mediaType || 'video'
      });
      
      // Add date range parameters
      if (dateRangeInfo.type === 'custom') {
        params.append('startDate', dateRangeInfo.startDate);
        params.append('endDate', dateRangeInfo.endDate);
        console.log('🔄 CreativeAttributes using CUSTOM date range:', dateRangeInfo.startDate, 'to', dateRangeInfo.endDate);
      } else {
        params.append('days', dateRangeInfo.days.toString());
        console.log('🔄 CreativeAttributes using PRESET date range:', dateRangeInfo.days, 'days');
      }
      
      // Add adNameFilter if present
      if (adNameFilter && adNameFilter.keywords && adNameFilter.keywords.length > 0) {
        params.append('adNameFilterKeywords', JSON.stringify(adNameFilter.keywords));
        params.append('adNameFilterLogic', adNameFilter.logic || 'AND');
        params.append('adNameFilterMode', adNameFilter.mode || 'include');
        console.log('✅ Adding ad name filter to API call:', adNameFilter);
      }
      
      console.log('🔍 Creative Attributes API call:', {
        url: `/api/feature-analysis/feature-combinations?${params}`,
        accountId,
        mediaType,
        correlationMetric,
        dateRangeInfo,
        adNameFilter,
        analysisType: mediaType || 'video'
      });
      
      const analysisResponse = await fetch(`/api/feature-analysis/feature-combinations?${params}`, {
        method: 'GET',
        headers: { 
          'Content-Type': 'application/json',
          'x-account-id': accountId
        }
      });

      if (!analysisResponse.ok) {
        throw new Error('Failed to analyze features');
      }

      const analysisResult = await analysisResponse.json();
      
      console.log('📥 Creative Attributes received from backend:', {
        hasAnalysis: !!analysisResult.analysis,
        hasSingleFeatures: !!analysisResult.analysis?.singleFeatures,
        hasFeaturePairs: !!analysisResult.analysis?.featurePairs,
        hasFeatureTriples: !!analysisResult.analysis?.featureTriples,
        totalAds: analysisResult.analysis?.totalAds,
        avgMetric: analysisResult.analysis?.avgMetric,
        singleFeaturesLength: analysisResult.analysis?.singleFeatures?.length,
        featurePairsLength: analysisResult.analysis?.featurePairs?.length,
        featureTriplesLength: analysisResult.analysis?.featureTriples?.length
      });
      
      // Debug: Check what media types are in the data
      if (analysisResult.data && analysisResult.data.length > 0) {
        const videoAds = analysisResult.data.filter(ad => ad.video_url).length;
        const imageAds = analysisResult.data.filter(ad => ad.image_url).length;
        console.log('📊 Media Types in Response:', { videoAds, imageAds, totalAds: analysisResult.data.length });
      }

      // Only set data if request wasn't aborted
      if (!abortControllerRef.current?.signal.aborted) {
        setFeatureAnalysisData(analysisResult);
      }
    } catch (error) {
      if (error.name === 'AbortError') {
        console.log('🛑 Feature analysis request was aborted');
      } else {
        console.error('Error running feature analysis:', error);
        setFeatureAnalysisData({
          error: error.message,
          totalAdsAnalyzed: 0,
          averageRevenue: 0,
          analysisType: 'all'
        });
        // Reset params on error to allow retry
        lastRequestParams.current = null;
      }
    } finally {
      // Only set loading to false if request wasn't aborted
      if (!abortControllerRef.current?.signal.aborted) {
        clearInterval(progressInterval);
        setAnalysisProgress(100);
      setLoading(false);
    }
      abortControllerRef.current = null;
    }
  }, [getAccountId, filters, correlationMetric, mediaType]);

  // Auto-run analysis when component mounts or parameters change
  useEffect(() => {
    if (!filters) {
      console.log('⏳ CreativeAttributes waiting for filters...');
      return;
    }
    console.log('🔄 CreativeAttributes filters changed, reloading...', filters);
    console.log('🔄 Full filters object:', JSON.stringify(filters, null, 2));
    console.log('🔄 adNameFilterKeywords specifically:', filters?.adNameFilterKeywords);
    console.log('🔄 Filters timestamp:', Date.now());
    const accountId = getAccountId();
    if (!accountId) {
      console.log('🚫 Creative Attributes auto-run skipped: No account ID');
      return;
    }
    
    const dateRangeInfo = getDateRangeInfo();
    const adNameFilter = getAdNameFilter();
    const mediaType = getMediaType();
    
    // Create a unique key for the current parameters
    const currentParams = JSON.stringify({ dateRangeInfo, adNameFilter, mediaType, correlationMetric, accountId });
    
    console.log('🔄 Creative Attributes auto-run check:', {
      isInitialMount: isInitialMount.current,
      paramsChanged: lastRequestParams.current !== currentParams,
      hasExistingData: !!featureAnalysisData,
      currentParams: currentParams.substring(0, 100) + '...'
    });
    
    // Trigger on initial mount, or if parameters changed, or if no data exists (but not if already loading)
    const shouldRun = !loading && (
      isInitialMount.current || 
      lastRequestParams.current !== currentParams || 
      !featureAnalysisData
    );
    
    if (shouldRun) {
      lastRequestParams.current = currentParams;
      
      if (isInitialMount.current) {
        isInitialMount.current = false;
        console.log('🚀 Creative Attributes initial mount - triggering feature analysis');
      } else if (lastRequestParams.current !== currentParams) {
        console.log('🔄 Creative Attributes parameters changed - triggering feature analysis');
      } else {
        console.log('🔄 Creative Attributes no existing data - triggering feature analysis');
      }

      // Debounce the actual request
      if (debounceTimeoutRef.current) {
        clearTimeout(debounceTimeoutRef.current);
      }
      
      debounceTimeoutRef.current = setTimeout(() => {
        console.log('⏰ Creative Attributes debounced auto-run executing...');
        runFeatureAnalysis(true);
      }, 100);
    } else {
      console.log('⏭️ Creative Attributes auto-run skipped: Same parameters and data exists');
    }
  }, [filters, getAccountId, runFeatureAnalysis, correlationMetric, mediaType]);

  // Cleanup on unmount
  useEffect(() => {
    return () => {
      if (abortControllerRef.current) {
        abortControllerRef.current.abort();
      }
      if (debounceTimeoutRef.current) {
        clearTimeout(debounceTimeoutRef.current);
      }
    };
  }, []);


  // Open preview modal
  const openPreview = (assetUrl, mediaFormat, adName) => {
    console.log('🎬 Creative Analysis - Opening preview:', {
      assetUrl: assetUrl?.substring(0, 50) + '...',
      mediaFormat,
      adName,
      hasAdName: !!adName
    });
    setPreviewModal({
      isOpen: true,
      assetUrl: assetUrl,
      mediaFormat: mediaFormat,
      adName: adName || 'Unknown Ad'
    });
  };

  // Render thumbnail for example ads
  const renderThumbnail = (assetUrl, mediaFormat, adName, className = "w-16 h-16") => {
    if (!assetUrl) {
    return (
        <div className={`${className} bg-gray-100 rounded-lg flex items-center justify-center`}>
          <span className="text-xs text-gray-500 text-center">No Asset</span>
        </div>
      );
    }

    if (mediaFormat === 'Video') {
          return (
        <div 
          className={`${className} relative bg-gray-200 rounded-lg overflow-hidden cursor-pointer hover:scale-105 transition-transform`}
          onClick={() => openPreview(assetUrl, mediaFormat, adName)}
          title={`Click to preview ${adName}`}
        >
          <video
            src={assetUrl}
            className="w-full h-full object-cover"
            preload="auto"
            muted
            playsInline
            controls={false}
          />
          <div className="absolute inset-0 bg-black bg-opacity-50 flex items-center justify-center text-white text-2xl">
            ▶️
          </div>
            </div>
          );
    }

    return (
        <img
          src={assetUrl}
          alt={adName}
        className={`${className} object-cover rounded-lg cursor-pointer hover:scale-105 transition-transform`}
        onClick={() => openPreview(assetUrl, mediaFormat, adName)}
        title={`Click to preview ${adName}`}
      />
    );
  };

  // Modal Preview Component
  const renderPreviewModal = () => {
    if (!previewModal.isOpen) return null;

    const closeModal = () => {
      setPreviewModal({ isOpen: false, assetUrl: '', mediaFormat: '', adName: '' });
    };

    return (
      <div 
        className="fixed inset-0 bg-black bg-opacity-75 flex items-center justify-center z-[60] p-4"
        onClick={closeModal}
      >
        <div 
          className="bg-white rounded-lg max-w-4xl max-h-[90vh] overflow-hidden relative"
          onClick={(e) => e.stopPropagation()}
        >
          {/* Header */}
          <div className="flex justify-between items-center p-4 border-b border-gray-200">
            <h3 className="text-lg font-medium text-gray-900 truncate pr-4" title={previewModal.adName}>
              {previewModal.adName}
            </h3>
            <button
              onClick={closeModal}
              className="text-gray-400 hover:text-gray-600 transition-colors p-1 rounded-full hover:bg-gray-100 flex-shrink-0"
              aria-label="Close preview"
              title="Close"
            >
              <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
              </svg>
            </button>
          </div>
          
          {/* Content */}
          <div className="p-4">
            {previewModal.mediaFormat === 'Video' ? (
              <video 
                className="w-full h-auto max-h-[70vh] rounded-lg shadow-lg"
                controls
                autoPlay
                preload="metadata"
              >
                <source src={previewModal.assetUrl} type="video/mp4" />
                Your browser does not support the video tag.
              </video>
            ) : (
              <img 
                src={previewModal.assetUrl} 
                alt={previewModal.adName}
                className="w-full h-auto max-h-[70vh] object-contain rounded-lg shadow-lg"
              />
            )}
          </div>
          
          {/* Footer */}
          <div className="p-4 border-t border-gray-200 bg-gray-50">
            <div className="flex justify-between items-center">
              <span className="text-sm text-gray-600">
                {previewModal.mediaFormat === 'Video' ? '🎥 Video Asset' : '🖼️ Image Asset'}
              </span>
              <div className="flex space-x-3">
                <button
                  onClick={() => {
                    navigator.clipboard.writeText(previewModal.assetUrl).then(() => {
                      // Show a temporary success message
                      const btn = document.activeElement;
                      const originalText = btn.textContent;
                      btn.textContent = '✓ Copied!';
                      setTimeout(() => {
                        btn.textContent = originalText;
                      }, 2000);
                    }).catch(err => {
                      console.error('Failed to copy URL:', err);
                      alert('Failed to copy URL. Please try again.');
                    });
                  }}
                  className="px-3 py-1 text-sm text-green-600 hover:text-green-800 border border-green-300 rounded hover:bg-green-50 transition-colors"
                  title="Copy asset URL to clipboard"
                >
                  Copy URL
                </button>
                <button
                  onClick={closeModal}
                  className="px-3 py-1 text-sm text-gray-600 hover:text-gray-800 border border-gray-300 rounded hover:bg-gray-100 transition-colors"
                >
                  Close
                </button>
              </div>
            </div>
          </div>
        </div>
      </div>
    );
  };

  // Handle ESC key for modal
  useEffect(() => {
    const handleEsc = (e) => {
      if (e.key === 'Escape' && previewModal.isOpen) {
        setPreviewModal({ isOpen: false, assetUrl: '', mediaFormat: '', adName: '' });
      }
    };

    if (previewModal.isOpen) {
      document.addEventListener('keydown', handleEsc);
      document.body.style.overflow = 'hidden';
    }

    return () => {
      document.removeEventListener('keydown', handleEsc);
      document.body.style.overflow = 'unset';
    };
  }, [previewModal.isOpen]);

  if (loading) {
    return (
      <div className="space-y-6">
        {/* Loading State */}
        <div className="bg-blue-50 border border-blue-200 rounded-lg p-6">
          <div className="flex flex-col items-center justify-center">
            <div className="flex items-center mb-4">
              <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600 mr-3"></div>
              <div className="text-blue-800">
                <div className="font-medium">🔍 Feature Analysis in Progress...</div>
                <div className="text-sm">Analyzing feature combinations and correlations</div>
          </div>
        </div>
            {/* Progress Bar */}
            <div className="w-full max-w-md mb-4">
              <div className="flex justify-between items-center mb-2">
                <span className="text-sm font-medium text-blue-800">Analysis Progress</span>
                <span className="text-sm text-blue-600">{Math.round(analysisProgress)}%</span>
      </div>
              <div className="w-full bg-blue-200 rounded-full h-3">
                <div 
                  className="bg-blue-600 h-3 rounded-full transition-all duration-300 ease-out"
                  style={{ width: `${analysisProgress}%` }}
                ></div>
              </div>
            </div>
            
            <div className="text-center text-blue-700">
              <div className="text-sm mb-2">Expected completion: 30 seconds</div>
              <div className="text-xs text-blue-600 mb-3">The AI is analyzing feature combinations to identify winning patterns.</div>
            </div>
            <button
              onClick={() => {
                if (abortControllerRef.current) {
                  abortControllerRef.current.abort();
                }
                if (debounceTimeoutRef.current) {
                  clearTimeout(debounceTimeoutRef.current);
                }
                setLoading(false);
                lastRequestParams.current = null;
                console.log('🛑 Feature analysis cancelled by user');
              }}
              className="mt-4 px-4 py-2 text-sm text-blue-600 hover:text-blue-800 border border-blue-300 rounded hover:bg-blue-100 transition-colors"
            >
              Cancel Analysis
            </button>
          </div>
        </div>
      </div>
    );
  }

  if (!featureAnalysisData || featureAnalysisData.error) {
    return (
      <div className="space-y-6">
        <div className="text-center py-12">
          <div className="text-gray-400 text-6xl mb-4">🔍</div>
          <h3 className="text-lg font-medium text-gray-900 mb-2">No Feature Analysis Available</h3>
          <p className="text-gray-500 mb-4">
            {featureAnalysisData?.error 
              ? `Error: ${featureAnalysisData.error}`
              : "No ads found for feature analysis with the selected filters."
            }
          </p>
          <button
            onClick={() => {
              console.log('🔄 Manual retry triggered');
              runFeatureAnalysis(true);
            }}
            className="bg-blue-600 text-white px-4 py-2 rounded-lg hover:bg-blue-700 transition-colors"
          >
            Retry Analysis
          </button>
        </div>
      </div>
    );
  }

  const formatValue = (value, metric) => {
    if (value === null || value === undefined) return '0';
    const numValue = parseFloat(value);
    if (isNaN(numValue)) return '0';
    
    if (metric === 'revenue' || metric === 'spend' || metric === 'cpa') {
      return `$${numValue.toLocaleString()}`;
    } else if (metric === 'roas') {
      return `${numValue.toFixed(2)}x`;
    } else if (metric === 'ctr') {
      return `${numValue.toFixed(2)}%`;
    }
    return numValue.toFixed(2);
  };

  const formatFeatureName = (featureName) => {
    return featureName
      .replace(/^(cf_|f_)/, '') // Remove cf_ or f_ prefix
      .replace(/_/g, ' ') // Replace underscores with spaces
      .replace(/\b\w/g, l => l.toUpperCase()); // Capitalize each word
  };

    return (
      <div className="space-y-6">
      {/* Header */}
      <div className="flex justify-between items-center">
          <div>
          <h2 className="text-xl font-semibold text-gray-900">🔍 Feature Analysis</h2>
          <p className="text-sm text-gray-600 mt-1">AI analysis of feature combinations correlated with performance</p>
          </div>
        <div className="flex gap-3">
          {/* Media Type Selector */}
          <select
            value={mediaType}
            onChange={(e) => setMediaType(e.target.value)}
            className="px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
          >
            <option value="video">Video Only</option>
            <option value="image">Image Only</option>
          </select>
          
          {/* View Toggle */}
          {featureAnalysisData?.analysis?.[selectedAnalysisType] && featureAnalysisData.analysis[selectedAnalysisType].length > 0 && (
            <div className="flex items-center space-x-2 bg-gray-100 rounded-lg p-1">
              <button
                onClick={() => setViewMode('card')}
                className={`px-5 py-2.5 rounded-lg text-sm font-semibold transition-all duration-200 ${
                  viewMode === 'card'
                    ? 'bg-gradient-to-r from-indigo-500 to-purple-600 text-white shadow-lg transform scale-105'
                    : 'text-gray-600 hover:text-gray-900 hover:bg-white/50'
                }`}
              >
                Card
              </button>
              <button
                onClick={() => setViewMode('time-trend')}
                className={`px-5 py-2.5 rounded-lg text-sm font-semibold transition-all duration-200 ${
                  viewMode === 'time-trend'
                    ? 'bg-gradient-to-r from-indigo-500 to-purple-600 text-white shadow-lg transform scale-105'
                    : 'text-gray-600 hover:text-gray-900 hover:bg-white/50'
                }`}
              >
                Time Trend
              </button>
              <button
                onClick={() => setViewMode('comparison')}
                className={`px-5 py-2.5 rounded-lg text-sm font-semibold transition-all duration-200 ${
                  viewMode === 'comparison'
                    ? 'bg-gradient-to-r from-indigo-500 to-purple-600 text-white shadow-lg transform scale-105'
                    : 'text-gray-600 hover:text-gray-900 hover:bg-white/50'
                }`}
              >
                Comparison
              </button>
            </div>
          )}
        </div>
        </div>

      {/* AI Summary Section - Right after title */}
      {featureAnalysisData?.analysis?.[selectedAnalysisType] && featureAnalysisData.analysis[selectedAnalysisType].length > 0 && (
        <ReportSummary 
          reportType="creative-attributes"
          reportData={{
            items: featureAnalysisData.analysis[selectedAnalysisType],
            overallAvgMetric: featureAnalysisData.analysis?.avgMetric
          }}
          filters={filters}
          accountId={getAccountId()}
        />
      )}
        
      {/* Summary Stats */}
      <div className="bg-gray-50 border border-gray-200 rounded-lg p-4">
        <div className="grid grid-cols-2 gap-4">
          <div className="text-center">
            <div className="text-2xl font-bold text-blue-600">
              {featureAnalysisData.analysis?.totalAds || 0}
              </div>
            <div className="text-sm text-blue-700">Total Ads Analyzed</div>
              </div>
          <div className="text-center">
            <div className="text-2xl font-bold text-green-600">
              {formatValue(featureAnalysisData.analysis?.avgMetric, correlationMetric)}
            </div>
            <div className="text-sm text-green-700">Average {correlationMetric.toUpperCase()}</div>
          </div>
      </div>
      </div>

      {/* Analysis Type Selector */}
      <div className="bg-white border border-gray-200 rounded-lg p-4">
        <div className="flex items-center space-x-4">
          <span className="text-sm font-medium text-gray-700">Analysis Type:</span>
          <div className="flex space-x-2">
            {[
              { key: 'singleFeatures', label: 'Single' },
              { key: 'featurePairs', label: 'Pairs' },
              { key: 'featureTriples', label: 'Triples' }
            ].map(({ key, label }) => (
            <button
                key={key}
                onClick={() => setSelectedAnalysisType(key)}
                className={`px-3 py-1 text-sm rounded-lg transition-colors ${
                  selectedAnalysisType === key
                    ? 'bg-blue-600 text-white'
                    : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
                }`}
              >
                {label}
            </button>
          ))}
                  </div>
        </div>
      </div>

      {/* Feature Combinations Comparison Chart View */}
      {viewMode === 'comparison' && featureAnalysisData.analysis?.[selectedAnalysisType] && featureAnalysisData.analysis[selectedAnalysisType].length > 0 && (() => {
        const allCombinations = featureAnalysisData.analysis[selectedAnalysisType];
        // Sort by ROAS (totalRevenue / totalSpend) for consistent comparison
        const sortedCombinations = [...allCombinations].sort((a, b) => {
          const aRoas = parseFloat(a.totalSpend || 0) > 0 ? parseFloat(a.totalRevenue || 0) / parseFloat(a.totalSpend || 0) : 0;
          const bRoas = parseFloat(b.totalSpend || 0) > 0 ? parseFloat(b.totalRevenue || 0) / parseFloat(b.totalSpend || 0) : 0;
          return bRoas - aRoas; // Sort descending by ROAS
        });
        const top10 = sortedCombinations.slice(0, 10);
        
        // Helper function to format feature name for chart
        const getFeatureDisplayName = (combination) => {
          if (selectedAnalysisType === 'singleFeatures') {
            // Format: "Talent Actions:Speaking to Camera"
            return `${formatFeatureName(combination.feature || '')}: ${combination.value || ''}`;
          } else if (selectedAnalysisType === 'featurePairs') {
            // Format: "Talent Actions:Speaking to Camera + Setting:Indoor"
            const values = (combination.combination || '').split(' + ');
            const feature1 = formatFeatureName(combination.feature1 || '');
            const feature2 = formatFeatureName(combination.feature2 || '');
            return `${feature1}:${values[0] || ''} + ${feature2}:${values[1] || ''}`;
          } else if (selectedAnalysisType === 'featureTriples') {
            // Format: "Feature1:Value1 + Feature2:Value2 + Feature3:Value3"
            const values = (combination.combination || '').split(' + ');
            const feature1 = formatFeatureName(combination.feature1 || '');
            const feature2 = formatFeatureName(combination.feature2 || '');
            const feature3 = formatFeatureName(combination.feature3 || '');
            return `${feature1}:${values[0] || ''} + ${feature2}:${values[1] || ''} + ${feature3}:${values[2] || ''}`;
          }
          return combination.combination || 'Unknown Feature';
        };
        
        return (
          <div className="mb-6">
            <ComparisonChart
              title={`Top ${selectedAnalysisType === 'singleFeatures' ? 'Single' : selectedAnalysisType === 'featurePairs' ? 'Pairs' : 'Triples'} Features Comparison`}
              data={top10.map(combination => {
                const totalSpend = parseFloat(combination.totalSpend || 0);
                const totalRevenue = parseFloat(combination.totalRevenue || 0);
                const roas = totalSpend > 0 ? totalRevenue / totalSpend : 0;
                
                return {
                  name: getFeatureDisplayName(combination),
                  spend: totalSpend,
                  roas: roas
                };
              })}
              xAxisKey="name"
              metrics={[
                { key: 'spend', label: 'Spend', color: '#8b5cf6', format: 'currency', yAxisId: 'left' },
                { key: 'roas', label: 'ROAS', color: '#ec4899', format: 'number', yAxisId: 'right' }
              ]}
              height={400}
              maxItems={10}
            />
          </div>
        );
      })()}

      {/* Feature Combinations Card Grid View */}
      {viewMode === 'card' && featureAnalysisData.analysis?.[selectedAnalysisType] && featureAnalysisData.analysis[selectedAnalysisType].length > 0 && (
        <div>
          <div className="mb-4">
            <h3 className="text-lg font-semibold text-gray-900">
              🏆 Top 10 {selectedAnalysisType === 'singleFeatures' ? 'Single' : 
               selectedAnalysisType === 'featurePairs' ? 'Pairs' : 
               selectedAnalysisType === 'featureTriples' ? 'Triples' : 'Single'} Feature Analysis
            </h3>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-4">
            {(() => {
              const allCombinations = featureAnalysisData.analysis[selectedAnalysisType];
              // Filter out CPA = 0 (no purchases) when CPA is selected
              const filteredCombinations = correlationMetric === 'cpa' 
                ? allCombinations.filter(c => parseFloat(c.avgMetric || 0) > 0)
                : allCombinations;
              // For CPA, lower is better (ascending). For others, higher is better (descending)
              const sortedCombinations = [...filteredCombinations].sort((a, b) => 
                correlationMetric === 'cpa' ? a.avgMetric - b.avgMetric : b.avgMetric - a.avgMetric
              );
              const top10 = sortedCombinations.slice(0, 10);
              
              const avgMetric = parseFloat(featureAnalysisData.analysis?.avgMetric || 0);
              
              return top10.map((combination, index) => {
                const originalIndex = allCombinations.findIndex(c => c === combination);
                const combinationKey = `${selectedAnalysisType}-${originalIndex}`;
                // Get first example ad for preview
                const exampleAds = combination.exampleAds || (combination.exampleAd ? [combination.exampleAd] : []);
                const previewAd = exampleAds.length > 0 ? exampleAds[0] : null;
                
                // Calculate delta percentage vs average
                const combinationMetric = parseFloat(combination.avgMetric || 0);
                let deltaPercent = 0;
                if (avgMetric > 0) {
                  if (correlationMetric === 'cpa') {
                    // For CPA, lower is better, so invert the calculation
                    deltaPercent = ((avgMetric - combinationMetric) / avgMetric) * 100;
                  } else {
                    // For other metrics, higher is better
                    deltaPercent = ((combinationMetric - avgMetric) / avgMetric) * 100;
                  }
                }
                
                const isTop3 = index < 3;
              
                return (
                  <div key={index} className={`relative rounded-2xl overflow-hidden transition-all duration-300 transform hover:scale-[1.02] hover:shadow-2xl group ${
                    isTop3
                      ? 'bg-gradient-to-br from-cyan-50 via-white to-cyan-50 border-2 border-cyan-300 shadow-lg ring-2 ring-cyan-200 ring-opacity-50'
                      : 'bg-white border border-gray-200 hover:border-gray-300 shadow-sm'
                  }`}>
                    {/* Rank Badge for Top 3 */}
                    {isTop3 && (
                      <div className="absolute top-3 left-3 z-10 w-10 h-10 flex items-center justify-center rounded-full bg-gradient-to-br from-cyan-500 to-blue-600 text-white text-base font-bold shadow-xl ring-2 ring-white">
                        {index + 1}
                      </div>
                    )}
                    
                    {/* Media Preview */}
                    <div className={`relative aspect-square overflow-hidden ${
                      isTop3 ? 'bg-gradient-to-br from-cyan-100 to-cyan-50' : 'bg-gray-100'
                    }`}>
                      {isTop3 && (
                        <div className="absolute inset-0 z-0 opacity-20 bg-gradient-to-br from-cyan-400 to-cyan-300" />
                      )}
                      {previewAd && (previewAd.image_url || previewAd.video_url) ? (
                        <div 
                          className="w-full h-full relative cursor-pointer"
                          onClick={(e) => {
                            e.stopPropagation();
                            openPreview(previewAd.image_url || previewAd.video_url, previewAd.image_url ? 'Image' : 'Video', previewAd.ad_name);
                          }}
                          title="Click to preview"
                        >
                          {renderThumbnail(previewAd.image_url || previewAd.video_url, previewAd.image_url ? 'Image' : 'Video', previewAd.ad_name, "w-full h-full")}
                        </div>
                      ) : (
                        <div className="w-full h-full relative bg-gradient-to-br from-cyan-400 to-cyan-600 flex items-center justify-center">
                          <div className="text-white text-4xl">🔍</div>
                          <div className="absolute top-2 right-2 bg-gradient-to-r from-cyan-500 to-cyan-600 text-white text-xs px-2.5 py-1 rounded-full font-semibold shadow-lg">
                            Feature
                          </div>
                        </div>
                      )}
                    </div>
                    
                    {/* Card Content */}
                    <div className={`p-5 ${isTop3 ? 'bg-white/80 backdrop-blur-sm' : ''}`}>
                      {/* Feature Name */}
                      <h4 className={`font-bold text-base mb-3 line-clamp-2 min-h-[2.5rem] ${
                        isTop3 ? 'text-cyan-900' : 'text-gray-900'
                      }`} title={
                        selectedAnalysisType === 'singleFeatures' 
                          ? `${formatFeatureName(combination.feature)}: ${combination.value}`
                          : combination.combination || 'Unknown Feature'
                      }>
                        {(() => {
                          if (selectedAnalysisType === 'singleFeatures') {
                            return (
                              <>
                                <span className="text-blue-600">{formatFeatureName(combination.feature)}</span>
                                <span className="text-gray-500 mx-1">:</span>
                                <span className="text-gray-800">{combination.value}</span>
                              </>
                            );
                          } else if (selectedAnalysisType === 'featurePairs') {
                            const values = combination.combination.split(' + ');
                            return (
                              <>
                                <span className="text-blue-600">{formatFeatureName(combination.feature1)}</span>
                                <span className="text-gray-500 mx-1">:</span>
                                <span className="text-gray-800">{values[0]}</span>
                                <span className="text-gray-400 mx-1">+</span>
                                <span className="text-blue-600">{formatFeatureName(combination.feature2)}</span>
                                <span className="text-gray-500 mx-1">:</span>
                                <span className="text-gray-800">{values[1]}</span>
                              </>
                            );
                          } else if (selectedAnalysisType === 'featureTriples') {
                            const values = combination.combination.split(' + ');
                            return (
                              <>
                                <span className="text-blue-600">{formatFeatureName(combination.feature1)}</span>
                                <span className="text-gray-500 mx-1">:</span>
                                <span className="text-gray-800">{values[0]}</span>
                                <span className="text-gray-400 mx-1">+</span>
                                <span className="text-blue-600">{formatFeatureName(combination.feature2)}</span>
                                <span className="text-gray-500 mx-1">:</span>
                                <span className="text-gray-800">{values[1]}</span>
                                <span className="text-gray-400 mx-1">+</span>
                                <span className="text-blue-600">{formatFeatureName(combination.feature3)}</span>
                                <span className="text-gray-500 mx-1">:</span>
                                <span className="text-gray-800">{values[2]}</span>
                              </>
                            );
                          }
                          return combination.combination || 'Unknown Feature Combination';
                        })()}
                      </h4>
                      
                      {/* Metrics Tags */}
                      <div className="space-y-3">
                        {/* Primary Metrics Row */}
                        <div className="flex flex-wrap gap-2">
                          <div className={`inline-flex items-center px-3 py-1.5 rounded-lg text-xs font-bold shadow-sm transition-all ${
                            correlationMetric === 'roas' 
                              ? isTop3
                                ? 'bg-gradient-to-r from-cyan-500 to-cyan-600 text-white'
                                : 'bg-green-100 text-green-800 border-2 border-green-300'
                              : 'bg-gray-100 text-gray-700'
                          }`}>
                            <span className="mr-1.5">Avg {correlationMetric.toUpperCase()}:</span> 
                            <span className="text-sm">{formatValue(combination.avgMetric, correlationMetric)}</span>
                          </div>
                          {!isNaN(deltaPercent) && avgMetric > 0 && (
                            <div className={`inline-flex items-center px-3 py-1.5 rounded-lg text-xs font-bold shadow-sm transition-all ${
                              deltaPercent > 0 
                                ? isTop3
                                  ? 'bg-gradient-to-r from-emerald-500 to-emerald-600 text-white'
                                  : 'bg-green-100 text-green-800 border-2 border-green-300'
                                : deltaPercent < 0
                                ? isTop3
                                  ? 'bg-gradient-to-r from-red-500 to-red-600 text-white'
                                  : 'bg-red-100 text-red-800 border-2 border-red-300'
                                : 'bg-gray-100 text-gray-700'
                            }`}>
                              <span className="mr-1.5">
                                {deltaPercent > 0 ? '↑' : deltaPercent < 0 ? '↓' : '='}
                              </span>
                              <span className="text-sm">{deltaPercent > 0 ? '+' : ''}{deltaPercent.toFixed(1)}%</span>
                            </div>
                          )}
                        </div>
                        
                        {/* Secondary Metrics Row */}
                        <div className="flex flex-wrap gap-2">
                          <div className={`inline-flex items-center px-3 py-1.5 rounded-lg text-xs font-bold shadow-sm ${
                            isTop3 ? 'bg-gradient-to-r from-cyan-500 to-cyan-600 text-white' : 'bg-gray-100 text-gray-700'
                          }`}>
                            <span className="mr-1.5">Ads:</span> 
                            <span className="text-sm">{combination.count || combination.adCount || 0}</span>
                          </div>
                        </div>
                      </div>
                    </div>
                    
                    {/* Folder Button - Bottom Right */}
                    <div className="absolute bottom-4 right-4 opacity-0 group-hover:opacity-100 transition-all duration-300 transform group-hover:scale-110">
                      <button
                        onClick={async (e) => {
                          e.stopPropagation();
                          const currentCombination = combination;
                          
                          // Show modal immediately with example ads
                          setAdsModal({
                            isOpen: true,
                            combination: currentCombination,
                            ads: exampleAds,
                            loading: true
                          });
                          
                          // Fetch all ads for this feature combination
                          try {
                            const allAdIds = currentCombination.all_ad_ids || [];
                            if (allAdIds.length > 0) {
                              // Deduplicate ad IDs
                              const uniqueAdIds = [...new Set(allAdIds.map(id => String(id)).filter(Boolean))];
                              
                              // Fetch creative data for all ads
                              const creativeData = await genericClusteringService.getCreativeData(uniqueAdIds);
                              
                              // Fetch performance data
                              let performanceData = [];
                              try {
                                performanceData = await genericClusteringService.getTopAdsByMetric(
                                  uniqueAdIds,
                                  uniqueAdIds.length,
                                  correlationMetric,
                                  filters?.dateRange
                                );
                              } catch (perfError) {
                                console.warn('Could not fetch performance data:', perfError);
                              }
                              
                              // Create maps for merging - also preserve data from exampleAds
                              const perfMap = new Map();
                              performanceData.forEach(ad => {
                                const adId = String(ad.raw_ad_id || ad.ad_id);
                                if (adId) {
                                  perfMap.set(adId, ad);
                                }
                              });
                              
                              // Create a map of exampleAds to preserve their performance data
                              const exampleAdsMap = new Map();
                              exampleAds.forEach(ad => {
                                const adId = String(ad.raw_ad_id || ad.ad_id);
                                if (adId) {
                                  exampleAdsMap.set(adId, ad);
                                }
                              });
                              
                              // Merge creative and performance data, preserving exampleAds data as fallback
                              const allAds = creativeData.map(creative => {
                                const adId = String(creative.raw_ad_id || creative.ad_id);
                                const perf = perfMap.get(adId);
                                const exampleAd = exampleAdsMap.get(adId);
                                
                                // Use performance data from API, fallback to exampleAd, then 0
                                return {
                                  raw_ad_id: creative.raw_ad_id || creative.ad_id,
                                  ad_id: creative.raw_ad_id || creative.ad_id,
                                  ad_name: creative.ad_name || exampleAd?.ad_name || 'Unknown Ad',
                                  asset_url: perf?.asset_url || exampleAd?.asset_url || creative.asset_url || creative.image_url || creative.video_url,
                                  image_url: creative.image_url || creative.asset_url || perf?.asset_url || exampleAd?.image_url,
                                  video_url: creative.video_url || creative.asset_url || perf?.asset_url || exampleAd?.video_url,
                                  media_format: creative.media_format || perf?.media_format || exampleAd?.media_format || 'Unknown',
                                  roas: parseFloat(perf?.roas ?? exampleAd?.roas ?? 0),
                                  total_spend: parseFloat(perf?.total_spend ?? exampleAd?.total_spend ?? 0),
                                  total_revenue: parseFloat(perf?.total_revenue ?? exampleAd?.total_revenue ?? 0),
                                  ctr: parseFloat(perf?.ctr ?? exampleAd?.ctr ?? 0),
                                  headline: creative.headline || perf?.headline || exampleAd?.headline,
                                  primary_text: creative.primary_text || perf?.primary_text || exampleAd?.primary_text,
                                  description: creative.description || perf?.description || exampleAd?.description,
                                  audio_text_content: creative.audio_text_content || perf?.audio_text_content || exampleAd?.audio_text_content,
                                  text_overlay_content: creative.text_overlay_content || perf?.text_overlay_content || exampleAd?.text_overlay_content
                                };
                              });
                              
                              // Remove duplicates
                              const uniqueAds = [];
                              const seenIds = new Set();
                              for (const ad of allAds) {
                                const adId = String(ad.raw_ad_id || ad.ad_id);
                                if (adId && !seenIds.has(adId)) {
                                  seenIds.add(adId);
                                  uniqueAds.push(ad);
                                }
                              }
                              
                              // Sort by performance metric
                              uniqueAds.sort((a, b) => {
                                const aValue = correlationMetric === 'roas' ? parseFloat(a.roas || 0) :
                                              correlationMetric === 'spend' ? parseFloat(a.total_spend || 0) :
                                              correlationMetric === 'revenue' ? parseFloat(a.total_revenue || 0) :
                                              correlationMetric === 'ctr' ? parseFloat(a.ctr || 0) : 0;
                                const bValue = correlationMetric === 'roas' ? parseFloat(b.roas || 0) :
                                              correlationMetric === 'spend' ? parseFloat(b.total_spend || 0) :
                                              correlationMetric === 'revenue' ? parseFloat(b.total_revenue || 0) :
                                              correlationMetric === 'ctr' ? parseFloat(b.ctr || 0) : 0;
                                return correlationMetric === 'cpa' ? aValue - bValue : bValue - aValue;
                              });
                              
                              // Update modal
                              setAdsModal(prev => {
                                if (prev.isOpen && prev.combination === currentCombination) {
                                  return {
                                    ...prev,
                                    ads: uniqueAds,
                                    loading: false
                                  };
                                }
                                return prev;
                              });
                            } else {
                              // No ad IDs, just use example ads
                              setAdsModal(prev => {
                                if (prev.isOpen && prev.combination === currentCombination) {
                                  return {
                                    ...prev,
                                    loading: false
                                  };
                                }
                                return prev;
                              });
                            }
                          } catch (error) {
                            console.error('Error fetching all ads:', error);
                            setAdsModal(prev => {
                              if (prev.isOpen && prev.combination === currentCombination) {
                                return {
                                  ...prev,
                                  loading: false
                                };
                              }
                              return prev;
                            });
                          }
                        }}
                        className="bg-blue-600 hover:bg-blue-700 text-white p-2 rounded-full shadow-lg pointer-events-auto"
                        title="View all ads with this feature"
                      >
                        <Folder className="w-5 h-5" />
                      </button>
                    </div>
                  </div>
                );
              });
            })()}
          </div>
        </div>
      )}

      {/* Time Series Performance Chart View */}
      {viewMode === 'time-trend' && !loading && featureAnalysisData && featureAnalysisData.analysis && featureAnalysisData.analysis[selectedAnalysisType] && (() => {
        const combinations = featureAnalysisData.analysis[selectedAnalysisType] || [];
        const groups = combinations.map((combination, index) => {
          // Use all_ad_ids from backend (contains ALL ads, not just examples)
          const adIds = combination.all_ad_ids || [];
          
          // Generate combination name (format feature names properly)
          let combinationName = '';
          if (selectedAnalysisType === 'singleFeatures') {
            combinationName = `${formatFeatureName(combination.feature || '')}: ${combination.value || ''}`;
          } else if (selectedAnalysisType === 'featurePairs') {
            // Format: "Talent Actions:Speaking to Camera + Setting:Indoor"
            const values = (combination.combination || '').split(' + ');
            const feature1 = formatFeatureName(combination.feature1 || '');
            const feature2 = formatFeatureName(combination.feature2 || '');
            combinationName = `${feature1}:${values[0] || ''} + ${feature2}:${values[1] || ''}`;
          } else if (selectedAnalysisType === 'featureTriples') {
            // Format: "Feature1:Value1 + Feature2:Value2 + Feature3:Value3"
            const values = (combination.combination || '').split(' + ');
            const feature1 = formatFeatureName(combination.feature1 || '');
            const feature2 = formatFeatureName(combination.feature2 || '');
            const feature3 = formatFeatureName(combination.feature3 || '');
            combinationName = `${feature1}:${values[0] || ''} + ${feature2}:${values[1] || ''} + ${feature3}:${values[2] || ''}`;
          } else {
            combinationName = combination.combination || `Combination ${index + 1}`;
          }
          
          console.log('📊 Creative Analysis Chart Group:', {
            name: combinationName,
            dashboardCount: combination.count,
            dashboardAvgMetric: combination.avgMetric,
            chartAdIdsCount: adIds.length,
            dateRange: filters?.dateRange || 7
          });
          
          return {
            name: combinationName,
            adIds: adIds
          };
        }).filter(group => group.adIds.length > 0);
        
        return groups.length > 0 && (
          <div className="mt-6">
            <GroupTimeSeriesChart 
              accountId={getAccountId()}
              groups={groups}
              dateRangeDays={filters?.dateRange || 7}
              numberOfPeriods={14}
              groupType="Feature Combination"
              startDate={filters?.dateRangeType === 'custom' ? filters?.startDate : null}
              endDate={filters?.dateRangeType === 'custom' ? filters?.endDate : null}
            />
          </div>
        );
      })()}

      {/* Asset Preview Modal */}
      {renderPreviewModal()}

      {/* All Ads Modal */}
      {adsModal.isOpen && (
        <div className="fixed inset-0 bg-black bg-opacity-75 flex items-center justify-center z-50 p-4" onClick={() => setAdsModal({ isOpen: false, combination: null, ads: [], loading: false })}>
          <div className="bg-white rounded-lg max-w-6xl w-full max-h-[90vh] overflow-hidden flex flex-col" onClick={(e) => e.stopPropagation()}>
            {/* Header */}
            <div className="flex justify-between items-center p-6 border-b border-gray-200">
              <div>
                <h3 className="text-xl font-semibold text-gray-900">
                  All Ads: {(() => {
                    if (selectedAnalysisType === 'singleFeatures') {
                      return `${formatFeatureName(adsModal.combination?.feature)}: ${adsModal.combination?.value}`;
                    }
                    return adsModal.combination?.combination || 'Unknown Feature';
                  })()}
                </h3>
                <p className="text-sm text-gray-600 mt-1">
                  {adsModal.ads.length} ads with this feature combination
                </p>
              </div>
              <button 
                onClick={() => setAdsModal({ isOpen: false, combination: null, ads: [], loading: false })}
                className="text-gray-400 hover:text-gray-600 text-2xl"
              >
                ✕
              </button>
            </div>
            
            {/* Content - Grid of Ads */}
            <div className="flex-1 overflow-y-auto p-6">
              {adsModal.loading && adsModal.ads.length === 0 ? (
                <div className="flex items-center justify-center py-12">
                  <div className="text-center">
                    <div className="inline-block animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600 mb-4"></div>
                    <p className="text-gray-500">Loading ads...</p>
                  </div>
                </div>
              ) : adsModal.ads.length > 0 ? (
                <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
                  {adsModal.ads.map((ad, index) => {
                    const assetUrl = ad.asset_url || ad.image_url || ad.video_url;
                    const mediaFormat = ad.media_format || (ad.image_url ? 'Image' : 'Video');
                    
                    return (
                      <div key={index} className="border border-gray-200 rounded-lg overflow-hidden hover:shadow-lg transition-all bg-white">
                        {/* Media Preview */}
                        {assetUrl && (
                          <div 
                            className="relative bg-gray-100 aspect-square cursor-pointer"
                            onClick={() => openPreview(assetUrl, mediaFormat, ad.ad_name)}
                            title="Click to preview"
                          >
                            {renderThumbnail(assetUrl, mediaFormat, ad.ad_name, "w-full h-full")}
                          </div>
                        )}
                        
                        {/* Ad Info */}
                        <div className="p-4">
                          <h4 className="font-semibold text-gray-900 text-sm mb-2 line-clamp-2" title={ad.ad_name}>
                            {ad.ad_name || 'Unknown Ad'}
                          </h4>
                          <div className="flex flex-wrap gap-2 text-xs">
                            <div className="inline-flex items-center px-2 py-1 rounded bg-gray-100 text-gray-700">
                              <span className="font-semibold mr-1">Revenue:</span> {formatValue(ad.total_revenue, 'revenue')}
                            </div>
                            <div className="inline-flex items-center px-2 py-1 rounded bg-gray-100 text-gray-700">
                              <span className="font-semibold mr-1">ROAS:</span> {formatValue(ad.roas, 'roas')}
                            </div>
                            <div className="inline-flex items-center px-2 py-1 rounded bg-gray-100 text-gray-700">
                              <span className="font-semibold mr-1">CTR:</span> {formatValue(ad.ctr, 'ctr')}
                            </div>
                            <div className="inline-flex items-center px-2 py-1 rounded bg-gray-100 text-gray-700">
                              <span className="font-semibold mr-1">Spend:</span> {formatValue(ad.total_spend, 'spend')}
                            </div>
                          </div>
                        </div>
                      </div>
                    );
                  })}
                </div>
              ) : (
                <div className="text-center py-12">
                  <div className="text-gray-400 text-6xl mb-4">🔍</div>
                  <p className="text-gray-500 text-lg">No ads found</p>
                </div>
              )}
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default CreativeAttributes;